import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import {
  AvatarDropdown,
  AvatarName,
  Question,
} from '@/components';
import { getAdminInfo } from '@/services/auth';
import { getMyPermissions } from '@/services/permission';
import { extractPermissionCodes, setCurrentPermissions } from '@/utils/permission';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import avatar from '@/assets/img/icon-128x128.png';
import { SUPER_ADMIN } from '@/constants';
import  '@/assets/css/reset.less';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';


/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  isSuperAdmin?: boolean;
}> {
  const fetchUserInfo = async () => {
    // 无 Token 时不要打 /info、/my，避免登录页被 401 拦截器反复整页刷新
    if (!localStorage.getItem('accessToken')) {
      return undefined;
    }
    try {
      // 并行获取用户信息和权限列表
      const [userInfoRes, permissionsRes] = await Promise.all([
        getAdminInfo({ skipErrorHandler: true }),
        getMyPermissions({ skipErrorHandler: true }),
      ]);

      if (userInfoRes.success && userInfoRes.data) {
        // 提取权限编码列表
        let permissionCodes: string[] = [];
        if (permissionsRes.success && permissionsRes.data) {
          permissionCodes = extractPermissionCodes(permissionsRes.data);
          // 保存权限列表到 localStorage
          setCurrentPermissions(permissionCodes);
        }

        // 判断是否为超级管理员（通过角色编码 super_admin）
        const roles = userInfoRes.data.roles || [];
        const isSuperAdmin = roles.some((role: API.RoleInfo) => role.roleCode === SUPER_ADMIN);

        // 转换为CurrentUser格式
        return {
          ...userInfoRes.data,
          name: userInfoRes.data.realName || userInfoRes.data.username,
          avatar: avatar,
          userid: String(userInfoRes.data.id),
          email: userInfoRes.data.email,
          phone: userInfoRes.data.mobile,
          permissions: permissionCodes,
          isSuperAdmin, // 添加超级管理员标识
        } as API.CurrentUser;
      }
    } catch (_error) {
      // Token 失效：只清本地状态；是否跳转由调用方 / 拦截器处理，避免登录页死循环
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('permissions');
      const onLoginPage =
        window.location.pathname === loginPath ||
        window.location.pathname.endsWith(loginPath);
      if (!onLoginPage) {
        history.push(loginPath);
      }
    }
    return undefined;
  };

  // 以浏览器真实路径为准（history 在首屏可能尚未同步）
  const pathname = window.location.pathname;
  const isAuthPage = [loginPath, '/user/register', '/user/register-result'].some(
    (path) => pathname === path || pathname.endsWith(path),
  );

  if (!isAuthPage) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      isSuperAdmin: currentUser?.isSuperAdmin || false,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    isSuperAdmin: false,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: false,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <a key="openapi" href="http://localhost:6068/swagger-ui/index.html" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </a>,
        ]
      : [],
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
    headerTitleRender: () => (
      <a
        href="/"
        style={{
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: 8,
          height: '100%',
          textDecoration: 'none',
        }}
      >
        <img
          src="/main-logo.png"
          alt="NeissAI"
          style={{
            display: 'block',
            height: 28,
            width: 'auto',
            maxWidth: 160,
            objectFit: 'contain',
          }}
        />
        <span
          style={{
            margin: 0,
            paddingBottom: 1,
            color: 'rgba(0, 0, 0, 0.45)',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.06em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          管理后台
        </span>
      </a>
    ),
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 开发走 Umi proxy；生产走同域 /api/，由 nginx 反代到后端
  baseURL: '',
  ...errorConfig,
};
